import React, { useEffect, useState } from 'react';
import { FaSearch, FaFilter,  FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaCreditCard } from 'react-icons/fa';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import { auth } from '../firebase/firebase';
import './Users.css';

export default function Users() {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [filterOptions, setFilterOptions] = useState({
        city: '',
        paymentType: ''
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        limit: 10
    });

    const fetchUsers = async (customFilters = null) => {
        setIsLoading(true);
        try {
            const token = await auth.currentUser.getIdToken();
            const filters = customFilters || filterOptions;
            
            let url = `${process.env.REACT_APP_API_URL}/admin/users?`;
            const params = new URLSearchParams();

            // Add pagination parameters
            params.append('page', pagination.currentPage);
            params.append('limit', pagination.limit);

            // Add search query if exists
            if (searchQuery.trim()) {
                params.append('query', searchQuery.trim());
            }

            // Add filter parameters
            if (filters.city) {
                params.append('city', filters.city);
            }
            if (filters.paymentType) {
                params.append('payment_type', filters.paymentType);
            }

            url += params.toString();

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch users');
            }

            const data = await response.json();
            
            setUsers(data.data || []);
            setPagination(prev => ({
                ...prev,
                currentPage: data.current_page || 1,
                totalPages: data.total_pages || 0,
                totalItems: data.total_rows || 0
            }));

        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to fetch users');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [pagination.currentPage]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        fetchUsers();
    };

    const handleFilterApply = () => {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        setShowFilterModal(false);
        fetchUsers();
    };

    const handleFilterReset = () => {
        const resetFilters = { city: '', paymentType: '' };
        setFilterOptions(resetFilters);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        setShowFilterModal(false);
        fetchUsers(resetFilters);
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({
            ...prev,
            currentPage: newPage
        }));
    };

    const handleUserClick = (user) => {
        setSelectedUser(user);
        setShowUserDetailsModal(true);
    };

    const getPaymentMethodDisplay = (type, bankName) => {
        if (type === '100') return bankName || 'Bank Transfer';
        if (type === '200') return 'GoPay';
        return 'Other';
    };

    const handleModalClick = (e) => {
        // Close modal if clicking the overlay (outside the modal content)
        if (e.target.className === 'modal-overlay') {
            setShowUserDetailsModal(false);
            setShowFilterModal(false);
        }
    };

    return (
        <div className="users-page">
            <ToastContainer
                position="bottom-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Bounce}
            />
            
            <div className="users-header">
                <div className="title-section">
                    <h1><FaUser className="header-icon" /> Users</h1>
                    <p className="text-muted">Manage and view all registered users</p>
                </div>
                <div className="actions">
                    <form onSubmit={handleSearch} className="search-bar">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by id, name, or email..."
                            className="search-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </form>
                    <button className="filter-btn" onClick={() => setShowFilterModal(true)}>
                        <FaFilter />
                        Filter
                        {(filterOptions.city || filterOptions.paymentType) && (
                            <div className='badge-filter'>
                                {(filterOptions.city ? 1 : 0) + (filterOptions.paymentType ? 1 : 0)}
                            </div>
                        )}
                    </button>
                </div>
            </div>

            <div className="users-table-container">
                {isLoading ? (
                    <div className="loading-spinner">Loading...</div>
                ) : users.length === 0 ? (
                    <div className="no-users pt-3 px-3">
                        <p>No users found</p>
                    </div>
                ) : (
                    <div className="users-grid">
                        {users.map(user => (
                            <div key={user.uid} className="user-card" onClick={() => handleUserClick(user)}>
                                <div className="user-card-header">
                                    <div className="user-avatar">
                                        {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                                    </div>
                                    <div className="user-name">
                                        <h3>{user.first_name} {user.last_name}</h3>
                                        <span className="user-id">ID: {user.uid}</span>
                                    </div>
                                </div>
                                <div className="user-card-body">
                                    <div className="user-info">
                                        <FaEnvelope /> {user.email}
                                    </div>
                                    <div className="user-info">
                                        <FaPhone /> {user.phone}
                                    </div>
                                    <div className="user-info">
                                        <FaMapMarkerAlt /> {user.city}
                                    </div>
                                    <div className="user-info">
                                        <FaCreditCard /> {getPaymentMethodDisplay(user.payment_type, user.beneficiary_bank_name)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Filter Modal */}
            {showFilterModal && (
                <div className="modal-overlay" onClick={handleModalClick}>
                    <div className="modal-content">
                        <h2>Filter Users</h2>
                        <div className="filter-options">
                            <div className="filter-section mb-4">
                                <h3 className="mb-3">Location</h3>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Filter by city"
                                    value={filterOptions.city}
                                    onChange={(e) => setFilterOptions(prev => ({
                                        ...prev,
                                        city: e.target.value
                                    }))}
                                />
                            </div>

                            <div className="filter-section mb-4">
                                <h3 className="mb-3">Payment Method</h3>
                                <select
                                    className="form-control"
                                    value={filterOptions.paymentType}
                                    onChange={(e) => setFilterOptions(prev => ({
                                        ...prev,
                                        paymentType: e.target.value
                                    }))}
                                >
                                    <option value="">All Payment Methods</option>
                                    <option value="100">Bank Transfer</option>
                                    <option value="200">E-Wallet</option>
                                </select>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button className="btn secondary" onClick={() => setShowFilterModal(false)}>
                                Cancel
                            </button>
                            <button className="btn danger" onClick={handleFilterReset}>
                                Reset
                            </button>
                            <button className="btn primary" onClick={handleFilterApply}>
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* User Details Modal */}
            {showUserDetailsModal && selectedUser && (
                <div className="modal-overlay" onClick={handleModalClick}>
                    <div className="modal-content">
                        <h2>User Details</h2>
                        <div className="user-details">
                            <div className="detail-section">
                                <h3>Personal Information</h3>
                                <div className="detail-grid">
                                    <div className="detail-item full-width">
                                        <label>User ID</label>
                                        <p>{selectedUser.uid}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>First Name</label>
                                        <p>{selectedUser.first_name}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>Last Name</label>
                                        <p>{selectedUser.last_name}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>Email</label>
                                        <p>{selectedUser.email}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>Phone</label>
                                        <p>{selectedUser.phone}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>NIK</label>
                                        <p>{selectedUser.nik}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>Identity Name</label>
                                        <p>{selectedUser.identity_name}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h3>Address Information</h3>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Full Address</label>
                                        <p>{selectedUser.address}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>City</label>
                                        <p>{selectedUser.city}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>Post Code</label>
                                        <p>{selectedUser.post_code}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>Country Code</label>
                                        <p>{selectedUser.country_code}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h3>Payment Information</h3>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Payment Type</label>
                                        <p>{selectedUser.payment_type === '100' ? 'Bank Transfer' : selectedUser.payment_type === '200' ? 'GoPay' : selectedUser.payment_type}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>Bank Code</label>
                                        <p>{selectedUser.beneficiary_bank_code}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>Bank Name</label>
                                        <p>{selectedUser.beneficiary_bank_name}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>Account Number</label>
                                        <p>{selectedUser.beneficiary_account}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>Account Name</label>
                                        <p>{selectedUser.beneficiary_account_name}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>Region Code</label>
                                        <p>{selectedUser.beneficiary_region_code}</p>
                                    </div>
                                    {selectedUser.bank_account_number && (
                                        <div className="detail-item">
                                            <label>Bank Account Number</label>
                                            <p>{selectedUser.bank_account_number}</p>
                                        </div>
                                    )}
                                    {selectedUser.bank_account_name && (
                                        <div className="detail-item">
                                            <label>Bank Account Name</label>
                                            <p>{selectedUser.bank_account_name}</p>
                                        </div>
                                    )}
                                    {selectedUser.beneficiary_virtual_account && (
                                        <div className="detail-item">
                                            <label>Virtual Account</label>
                                            <p>{selectedUser.beneficiary_virtual_account}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="detail-section">
                                <h3>Additional Information</h3>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Terms & Conditions Accepted</label>
                                        <p>{selectedUser.accept_terms_and_condition ? 'Yes' : 'No'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button className="btn primary" onClick={() => setShowUserDetailsModal(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="pagination">
                <button 
                    className="btn secondary"
                    disabled={pagination.currentPage === 1}
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                >
                    Previous
                </button>
                <span>
                    Page {pagination.currentPage} of {pagination.totalPages}
                    {pagination.totalItems > 0 && ` (${pagination.totalItems} total)`}
                </span>
                <button 
                    className="btn secondary"
                    disabled={pagination.currentPage === pagination.totalPages}
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                >
                    Next
                </button>
            </div>
        </div>
    );
}
